import React, { useState, useRef } from 'react';
import { useUnifiedAuth } from '../hooks/useUnifiedAuth';
import { useNavigate } from 'react-router-dom';
import SMSOTPAuth from './SMSOTPAuth';

const EnhancedAuthModal = ({ isOpen, onClose, defaultTab = 'login' }) => {
  const [activeTab, setActiveTab] = useState(defaultTab);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [profileImage, setProfileImage] = useState(null);
  const [profileImagePreview, setProfileImagePreview] = useState(null);
  const fileInputRef = useRef(null);
  
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    // Enhanced registration fields
    first_name: '',
    last_name: '',
    facility_name: '', // For provider accounts
    phone: '',
    date_of_birth: '',
    gender: '',
    userType: 'user', // user, doctor, provider
    google_maps_link: '', // For provider accounts
    // Professional fields
    specialization: '', // for doctors
    qualification: '', // for doctors
    clinicName: '', // for providers
    licenseNumber: '', // for doctors and providers
    experience_years: '', // for doctors
    consultation_fee: '', // for doctors
    bio: '', // for both doctors and providers
    languages: ['Arabic', 'English'], // for both doctors and providers
    
    // Enhanced Provider Registration Fields
    providerCategory: '', // Hospital, Healthcare Clinic, Dental, Beauty Clinic, Spa, Pharmacy
    providerSubcategory: '',
    address: '',
    area: '',
    governorate: '',
    website: '',
    description: '',
    specialities: [],
    services: [],
    languages_spoken: ['Arabic', 'English'],
    insurance_accepted: ['Kuwait Insurance Company (KIC)'],
    amenities: [],
    is_24_hours: false,
    emergency_services: false,
    has_parking: false,
    wheelchair_accessible: false,
    total_doctors: 1,
    total_beds: null,
    total_therapists: null,
    total_pharmacists: null,
    
    // Optional insurance fields (for users)
    insurance_company: '',
    insurance_number: '',
    insurance_type: '',
    coverage_type: ''
  });
  
  const { login } = useUnifiedAuth();
  const navigate = useNavigate();

  if (!isOpen) return null;

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleArrayInputChange = (field, value) => {
    const items = value.split(',').map(item => item.trim()).filter(item => item);
    setFormData({
      ...formData,
      [field]: items
    });
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) { // 5MB limit
        setError('Image size should be less than 5MB');
        return;
      }
      
      setProfileImage(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setProfileImagePreview(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: formData.email,
          password: formData.password
        }),
      });

      const data = await response.json();

      if (response.ok) {
        await login(data);
        onClose();
        navigate('/');
      } else {
        setError(data.message || 'Login failed');
      }
    } catch (err) {
      setError('Network error. Please try again.');
      console.error('Login error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Create a copy of formData and handle profile image
      let registrationData = { ...formData };
      
      // Handle profile image upload
      if (profileImage) {
        const reader = new FileReader();
        reader.onload = async (e) => {
          registrationData.profile_image = e.target.result; // Base64 string
          await submitRegistration(registrationData);
        };
        reader.readAsDataURL(profileImage);
      } else {
        await submitRegistration(registrationData);
      }
    } catch (err) {
      setError('Registration failed. Please try again.');
      console.error('Registration error:', err);
      setLoading(false);
    }
  };

  const submitRegistration = async (registrationData) => {
    try {
      // Remove DOB and Gender fields for providers
      if (formData.userType === 'provider') {
        delete registrationData.date_of_birth;
        delete registrationData.gender;
      }
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(registrationData),
      });

      const data = await response.json();

      if (response.ok) {
        await login(data);
        onClose();
        if (registrationData.userType === 'provider' || formData.userType === 'provider') {
          navigate('/profile');
        } else {
          navigate('/');
        }
      } else {
        // Handle validation errors from backend
        let errorMessage = 'Registration failed';
        
        if (data.detail) {
          if (Array.isArray(data.detail)) {
            const errorMessages = data.detail.map(error => {
              const field = error.loc ? error.loc[error.loc.length - 1] : 'field';
              const message = error.msg || 'Invalid value';
              return `${field}: ${message}`;
            });
            errorMessage = errorMessages.join('; ');
          } else if (typeof data.detail === 'string') {
            errorMessage = data.detail;
          } else if (typeof data.detail === 'object' && data.detail.msg) {
            errorMessage = data.detail.msg;
          }
        } else if (data.message) {
          errorMessage = data.message;
        }
        
        setError(errorMessage);
      }
    } catch (err) {
      setError('Network error. Please try again.');
      console.error('Registration error:', err);
    } finally {
      setLoading(false);
    }
  };

  const renderDoctorCardPreview = () => {
    if (formData.userType !== 'doctor') return null;
    
    return (
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm mb-6">
        <div className="p-4">
          <h4 className="text-sm font-medium text-gray-700 mb-3">Preview (How your card will look)</h4>
          <div className="flex items-start space-x-4">
            <div className="flex-shrink-0">
              {profileImagePreview ? (
                <img
                  src={profileImagePreview}
                  alt="Preview"
                  className="w-16 h-16 rounded-full border-2 border-gray-200 object-cover"
                />
              ) : (
                <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center">
                  <span className="text-gray-400 text-xl">👨‍⚕️</span>
                </div>
              )}
              <div className="relative -mt-2 -mr-2 float-right">
                <div className="bg-green-500 rounded-full p-1">
                  <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </div>
              </div>
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-lg font-semibold text-gray-900">
                Dr. {formData.first_name} {formData.last_name}
              </h3>
              <p className="text-blue-600 font-medium">
                {formData.specialization || 'Specialization'}
              </p>
              <p className="text-sm text-gray-600 mt-1">
                {formData.qualification || 'Qualification'}
              </p>
              <div className="flex items-center space-x-4 mt-2">
                <div className="flex items-center space-x-1">
                  <div className="flex">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <svg key={star} className="w-4 h-4 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                      </svg>
                    ))}
                  </div>
                  <span className="text-sm text-gray-600">4.8 (120 reviews)</span>
                </div>
                <div className="text-sm text-gray-600">
                  <span className="font-medium">{formData.experience_years || 0}</span> years experience
                </div>
              </div>
            </div>
            <div className="text-right">
              <div className="text-lg font-semibold text-gray-900">
                {formData.consultation_fee ? `$${formData.consultation_fee}` : 'Contact for pricing'}
              </div>
              <div className="text-xs text-gray-500">Consultation</div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderProviderCardPreview = () => {
    if (formData.userType !== 'provider') return null;
    
    return (
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm mb-6">
        <div className="p-4">
          <h4 className="text-sm font-medium text-gray-700 mb-3">Preview (How your card will look)</h4>
          <div className="flex items-start space-x-4">
            <div className="flex-shrink-0">
              {profileImagePreview ? (
                <img
                  src={profileImagePreview}
                  alt="Preview"
                  className="w-16 h-16 rounded-lg border border-gray-200 object-cover"
                />
              ) : (
                <div className="w-16 h-16 rounded-lg bg-blue-50 flex items-center justify-center text-2xl">
                  🏥
                </div>
              )}
              <div className="relative -mt-2 -mr-2 float-right">
                <div className="bg-green-500 rounded-full p-1">
                  <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </div>
              </div>
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-lg font-semibold text-gray-900">
                {formData.facility_name || 'Facility Name'}
              </h3>
              <div className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-50 text-blue-600 mb-2">
                <span className="mr-1">🏥</span>
                {formData.providerCategory || 'Healthcare Clinic'}
              </div>
              {formData.address && (
                <div className="flex items-start space-x-1 mt-1">
                  <svg className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  <p className="text-sm text-gray-600">{formData.address}</p>
                </div>
              )}
            </div>
            <div className="text-right">
              <div className="text-sm font-medium text-green-600">Open Now</div>
              <div className="text-xs text-gray-500">Hours vary</div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={onClose}></div>

        {/* Modal panel */}
        <div className="inline-block overflow-hidden text-left align-bottom transition-all transform bg-white rounded-lg shadow-xl sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-gray-900">
              {activeTab === 'login' ? 'Sign In' : 'Create Professional Account'}
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Tabs */}
          <div className="flex border-b border-gray-200">
            <button
              onClick={() => setActiveTab('login')}
              className={`flex-1 py-3 px-4 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'login'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-600 hover:text-blue-600'
              }`}
            >
              Sign In
            </button>
            <button
              onClick={() => setActiveTab('register')}
              className={`flex-1 py-3 px-4 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'register'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-600 hover:text-blue-600'
              }`}
            >
              Sign Up
            </button>
          </div>

          {/* Content */}
          <div className="p-6">
            {error && (
              <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
                {error}
              </div>
            )}

            <form onSubmit={activeTab === 'login' ? handleLogin : handleRegister}>
              {activeTab === 'register' && (
                <>
                  {/* Account Type Selection */}
                  <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      Choose Account Type
                    </label>
                    <div className="grid grid-cols-3 gap-4">
                      {['user', 'doctor', 'provider'].map((type) => (
                        <label key={type} className="relative">
                          <input
                            type="radio"
                            name="userType"
                            value={type}
                            checked={formData.userType === type}
                            onChange={handleInputChange}
                            className="sr-only"
                          />
                          <div className={`p-4 rounded-lg border-2 cursor-pointer transition-all text-center ${
                            formData.userType === type
                              ? 'border-blue-500 bg-blue-50'
                              : 'border-gray-200 hover:border-gray-300'
                          }`}>
                            <div className="text-2xl mb-2">
                              {type === 'user' ? '👤' : type === 'doctor' ? '👨‍⚕️' : '🏥'}
                            </div>
                            <div className="font-medium capitalize">{type === 'user' ? 'User' : type}</div>
                            <div className="text-xs text-gray-500">
                              {type === 'user' ? 'Book appointments' :
                               type === 'doctor' ? 'Individual practice' :
                               'Healthcare facility'}
                            </div>
                          </div>
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Card Preview */}
                  {formData.userType === 'doctor' && renderDoctorCardPreview()}
                  {formData.userType === 'provider' && renderProviderCardPreview()}

                  {/* Profile Picture Upload */}
                  <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      Profile Picture
                    </label>
                    <div className="flex items-center space-x-4">
                      <div className="flex-shrink-0">
                        {profileImagePreview ? (
                          <img
                            src={profileImagePreview}
                            alt="Profile preview"
                            className={`w-20 h-20 object-cover border-2 border-gray-200 ${
                              formData.userType === 'doctor' ? 'rounded-full' : 'rounded-lg'
                            }`}
                          />
                        ) : (
                          <div className={`w-20 h-20 bg-gray-100 border-2 border-gray-200 flex items-center justify-center ${
                            formData.userType === 'doctor' ? 'rounded-full' : 'rounded-lg'
                          }`}>
                            <span className="text-gray-400 text-2xl">
                              {formData.userType === 'doctor' ? '👨‍⚕️' : 
                               formData.userType === 'provider' ? '🏥' : '👤'}
                            </span>
                          </div>
                        )}
                      </div>
                      <div className="flex-1">
                        <div className="flex space-x-3">
                          <button
                            type="button"
                            onClick={() => fileInputRef.current?.click()}
                            className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50"
                          >
                            Upload Now
                          </button>
                          <button
                            type="button"
                            className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-800"
                          >
                            Add Later
                          </button>
                        </div>
                        <p className="text-xs text-gray-500 mt-1">
                          Recommended: Square image, max 5MB
                        </p>
                      </div>
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        onChange={handleImageUpload}
                        className="hidden"
                      />
                    </div>
                  </div>

                  {/* Rest of the registration form - split into columns for better layout */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Left Column */}
                    <div className="space-y-4">
                      {/* Personal Information */}
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <h3 className="text-sm font-medium text-gray-700 mb-3">
                          {formData.userType === 'provider' ? 'Facility Information' : 'Personal Information'}
                        </h3>
                        
                        {formData.userType === 'provider' ? (
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Facility Name *
                            </label>
                            <input
                              type="text"
                              name="facility_name"
                              value={formData.facility_name}
                              onChange={handleInputChange}
                              placeholder="Enter your facility name"
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                              required
                            />
                          </div>
                        ) : (
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                First Name *
                              </label>
                              <input
                                type="text"
                                name="first_name"
                                value={formData.first_name}
                                onChange={handleInputChange}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                required
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                Last Name *
                              </label>
                              <input
                                type="text"
                                name="last_name"
                                value={formData.last_name}
                                onChange={handleInputChange}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                required
                              />
                            </div>
                          </div>
                        )}

                        <div className="mt-3">
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Kuwait Phone Number *
                          </label>
                          <input
                            type="tel"
                            name="phone"
                            value={formData.phone}
                            onChange={handleInputChange}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="+965 XXXXXXXX"
                            pattern="^\+965\s?\d{8}$"
                            required
                          />
                        </div>

                        {/* Date of Birth and Gender - Only for User and Doctor accounts */}
                        {formData.userType !== 'provider' && (
                          <div className="grid grid-cols-2 gap-3 mt-3">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                Date of Birth *
                              </label>
                              <input
                                type="date"
                                name="date_of_birth"
                                value={formData.date_of_birth}
                                onChange={handleInputChange}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                required
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                Gender *
                              </label>
                              <select
                                name="gender"
                                value={formData.gender}
                                onChange={handleInputChange}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                required
                              >
                                <option value="">Select Gender</option>
                                <option value="male">Male</option>
                                <option value="female">Female</option>
                                <option value="other">Other</option>
                              </select>
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Professional Information for Doctors */}
                      {formData.userType === 'doctor' && (
                        <div className="bg-blue-50 p-4 rounded-lg">
                          <h3 className="text-sm font-medium text-blue-700 mb-3">
                            Medical Professional Information
                          </h3>
                          
                          <div className="space-y-3">
                            <div>
                              <label className="block text-sm font-medium text-blue-700 mb-2">
                                Medical Specialization *
                              </label>
                              <input
                                type="text"
                                name="specialization"
                                value={formData.specialization}
                                onChange={handleInputChange}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                placeholder="e.g., Cardiology, Pediatrics, General Medicine"
                                required
                              />
                            </div>
                            
                            <div>
                              <label className="block text-sm font-medium text-blue-700 mb-2">
                                Qualification
                              </label>
                              <input
                                type="text"
                                name="qualification"
                                value={formData.qualification}
                                onChange={handleInputChange}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                placeholder="e.g., MBBS, MD, FRCS"
                              />
                            </div>
                            
                            <div className="grid grid-cols-2 gap-3">
                              <div>
                                <label className="block text-sm font-medium text-blue-700 mb-2">
                                  Experience (Years)
                                </label>
                                <input
                                  type="number"
                                  name="experience_years"
                                  value={formData.experience_years}
                                  onChange={handleInputChange}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                  placeholder="0"
                                  min="0"
                                />
                              </div>
                              
                              <div>
                                <label className="block text-sm font-medium text-blue-700 mb-2">
                                  Consultation Fee (KD)
                                </label>
                                <input
                                  type="number"
                                  name="consultation_fee"
                                  value={formData.consultation_fee}
                                  onChange={handleInputChange}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                  placeholder="0"
                                  min="0"
                                />
                              </div>
                            </div>
                            
                            <div>
                              <label className="block text-sm font-medium text-blue-700 mb-2">
                                Medical License Number *
                              </label>
                              <input
                                type="text"
                                name="licenseNumber"
                                value={formData.licenseNumber}
                                onChange={handleInputChange}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                placeholder="Kuwait Medical License Number"
                                required
                              />
                            </div>
                            
                            <div>
                              <label className="block text-sm font-medium text-blue-700 mb-2">
                                Bio/Description
                              </label>
                              <textarea
                                name="bio"
                                value={formData.bio}
                                onChange={handleInputChange}
                                rows="3"
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                placeholder="Brief description about your practice and expertise"
                              />
                            </div>
                            
                            <div>
                              <label className="block text-sm font-medium text-blue-700 mb-2">
                                Languages Spoken
                              </label>
                              <input
                                type="text"
                                value={formData.languages.join(', ')}
                                onChange={(e) => handleArrayInputChange('languages', e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                placeholder="Arabic, English, French"
                              />
                            </div>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Right Column */}
                    <div className="space-y-4">
                      {/* Provider Information */}
                      {formData.userType === 'provider' && (
                        <div className="bg-green-50 p-4 rounded-lg">
                          <h3 className="text-sm font-medium text-green-700 mb-3">
                            Healthcare Facility Details
                          </h3>
                          
                          <div className="space-y-3">
                            <div>
                              <label className="block text-sm font-medium text-green-700 mb-2">
                                License Number *
                              </label>
                              <input
                                type="text"
                                name="licenseNumber"
                                value={formData.licenseNumber}
                                onChange={handleInputChange}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                placeholder="Healthcare facility license number"
                                required
                              />
                            </div>
                            
                            <div className="grid grid-cols-2 gap-3">
                              <div>
                                <label className="block text-sm font-medium text-green-700 mb-2">
                                  Facility Category *
                                </label>
                                <select
                                  name="providerCategory"
                                  value={formData.providerCategory}
                                  onChange={handleInputChange}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                  required
                                >
                                  <option value="">Select Category</option>
                                  <option value="Hospital">Hospital</option>
                                  <option value="Healthcare Clinic">Healthcare Clinic</option>
                                  <option value="Dental Clinic">Dental Clinic</option>
                                  <option value="Beauty Clinic">Beauty Clinic</option>
                                  <option value="Salon">Salon (Ladies)</option>
                                  <option value="Barbershop">Barbershop (Men)</option>
                                  <option value="Spa">Spa</option>
                                  <option value="Pharmacy">Pharmacy</option>
                                </select>
                              </div>
                              
                              <div>
                                <label className="block text-sm font-medium text-green-700 mb-2">
                                  Google Maps Link *
                                </label>
                                <input
                                  type="url"
                                  name="google_maps_link"
                                  value={formData.google_maps_link}
                                  onChange={handleInputChange}
                                  placeholder="https://maps.google.com/..."
                                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                  required
                                />
                              </div>
                            </div>
                            
                            <div>
                              <label className="block text-sm font-medium text-green-700 mb-2">
                                Address *
                              </label>
                              <input
                                type="text"
                                name="address"
                                value={formData.address}
                                onChange={handleInputChange}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                placeholder="Complete facility address"
                                required
                              />
                            </div>
                            
                            <div className="grid grid-cols-2 gap-3">
                              <div>
                                <label className="block text-sm font-medium text-green-700 mb-2">
                                  Area *
                                </label>
                                <input
                                  type="text"
                                  name="area"
                                  value={formData.area}
                                  onChange={handleInputChange}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                  placeholder="e.g., Kuwait City, Salmiya"
                                  required
                                />
                              </div>
                              
                              <div>
                                <label className="block text-sm font-medium text-green-700 mb-2">
                                  Governorate *
                                </label>
                                <select
                                  name="governorate"
                                  value={formData.governorate}
                                  onChange={handleInputChange}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                  required
                                >
                                  <option value="">Select Governorate</option>
                                  <option value="Capital">Capital</option>
                                  <option value="Hawalli">Hawalli</option>
                                  <option value="Ahmadi">Ahmadi</option>
                                  <option value="Jahra">Jahra</option>
                                  <option value="Farwaniya">Farwaniya</option>
                                  <option value="Mubarak Al-Kabeer">Mubarak Al-Kabeer</option>
                                </select>
                              </div>
                            </div>
                            
                            <div>
                              <label className="block text-sm font-medium text-green-700 mb-2">
                                Services Offered
                              </label>
                              <input
                                type="text"
                                value={formData.services.join(', ')}
                                onChange={(e) => handleArrayInputChange('services', e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                placeholder="General Consultation, Surgery, Emergency Care"
                              />
                            </div>
                            
                            <div>
                              <label className="block text-sm font-medium text-green-700 mb-2">
                                Specialties
                              </label>
                              <input
                                type="text"
                                value={formData.specialities.join(', ')}
                                onChange={(e) => handleArrayInputChange('specialities', e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                placeholder="Cardiology, Orthopedics, Pediatrics"
                              />
                            </div>
                            
                            <div>
                              <label className="block text-sm font-medium text-green-700 mb-2">
                                Facility Description
                              </label>
                              <textarea
                                name="description"
                                value={formData.description}
                                onChange={handleInputChange}
                                rows="3"
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                placeholder="Brief description of your facility and services"
                              />
                            </div>
                            
                            <div>
                              <label className="block text-sm font-medium text-green-700 mb-2">
                                Website (Optional)
                              </label>
                              <input
                                type="url"
                                name="website"
                                value={formData.website}
                                onChange={handleInputChange}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                placeholder="https://www.yourfacility.com"
                              />
                            </div>
                            
                            <div>
                              <label className="block text-sm font-medium text-green-700 mb-2">
                                Facility Features
                              </label>
                              <div className="grid grid-cols-2 gap-2">
                                <label className="flex items-center">
                                  <input
                                    type="checkbox"
                                    name="has_parking"
                                    checked={formData.has_parking}
                                    onChange={handleInputChange}
                                    className="mr-2"
                                  />
                                  <span className="text-sm">Parking Available</span>
                                </label>
                                <label className="flex items-center">
                                  <input
                                    type="checkbox"
                                    name="wheelchair_accessible"
                                    checked={formData.wheelchair_accessible}
                                    onChange={handleInputChange}
                                    className="mr-2"
                                  />
                                  <span className="text-sm">Wheelchair Accessible</span>
                                </label>
                                <label className="flex items-center">
                                  <input
                                    type="checkbox"
                                    name="emergency_services"
                                    checked={formData.emergency_services}
                                    onChange={handleInputChange}
                                    className="mr-2"
                                  />
                                  <span className="text-sm">Emergency Services</span>
                                </label>
                                <label className="flex items-center">
                                  <input
                                    type="checkbox"
                                    name="is_24_hours"
                                    checked={formData.is_24_hours}
                                    onChange={handleInputChange}
                                    className="mr-2"
                                  />
                                  <span className="text-sm">24/7 Operation</span>
                                </label>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Account Information */}
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <h3 className="text-sm font-medium text-gray-700 mb-3">Account Information</h3>
                        
                        <div className="space-y-3">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Email *
                            </label>
                            <input
                              type="email"
                              name="email"
                              value={formData.email}
                              onChange={handleInputChange}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                              required
                            />
                          </div>
                          
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Password *
                            </label>
                            <input
                              type="password"
                              name="password"
                              value={formData.password}
                              onChange={handleInputChange}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                              required
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              )}

              {/* Login Form */}
              {activeTab === 'login' && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Email
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Password
                    </label>
                    <input
                      type="password"
                      name="password"
                      value={formData.password}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>
                </div>
              )}

              <div className="mt-6 flex space-x-3">
                <button
                  type="button"
                  onClick={onClose}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 transition-colors"
                >
                  {loading ? 'Please wait...' : (activeTab === 'login' ? 'Sign In' : 'Create Account')}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EnhancedAuthModal;